'use strict';
import DataModelHelper from './data-model-helper';
import { DataModelArray, Relation } from './data-model-base-objects';
import { DataModelPropertyDirection } from './enumerations';

var constants = {
	windowAlias: 'w',
	calcValueSuffix: '_$$calcValue',
	defaultExpression: 'return $value;',
	valueProperty: '$value',
	oldValueProperty: '$oldValue',
	originalProperty: '$originalValue',
	newValueProperty: '$newValue',
	hasChangesProperty: '$hasChanges',
	targetProperty: '$target',
	triggeredBy: '$triggeredBy',
	configProperty: '$config',
	forceValueChanged: '$forceValueChanged',
	preventValueChanged: '$preventValueChanged',
	setValue: '$setValue',
	maximumRecalculationsAllowed: 50,
	currentThemeColors: '$currentThemeColors',
	filterExpressionDefaultDdValue: 'id=@id:guid',
	filterExpressionDefaultDqObjectIdValue: '[Expression-ObjectID]=@objectId:guid',
	filterExpressionDefaultDqFragmentIdsValue: 'id in (@id:guid[])',
};

var DataModelSystemProperty = {
	SourceState: '$sourceState',
	First: '$first',
	Count: 'Count',
	ObjectId: 'ObjectId',
	Return: 'return',
	Item: '$item',
	Inactive: '$inactive',
	Id: '$id',
	ErrorMessage: '$errorMessage',
	CatchError: '$catchError',
	Shared: 'Shared',
	Filter: '$filter',
	OrderBy: '$orderBy',
	PageNumber: '$pageNumber',
	PageSize: '$pageSize',
	TotalCount: '$totalCount',
	OuterContext: 'OuterContext',
	Parameters: '$parameters',
	FragmentIds: 'FragmentIds',
};

var MxDataModelDefaultValues = {
	IntType: 0,
	GuidType: undefined,
	ArrayType: [],
	ObjectType: {},
	StringType: '',
	DateType: null,
	BoolType: false,
	Currency: { CC: '', Value: null },
};

constants.initilizationCode =
	'var ' +
	constants.windowAlias +
	'  = window;' +
	'var ' +
	constants.originalProperty +
	' = v.defaultValue;' +
	'var ' +
	constants.oldValueProperty +
	' = v.oldValue;' +
	'var ' +
	constants.valueProperty +
	'  = v.value;' +
	'var ' +
	constants.configProperty +
	'  = c;';

DataModelCalculatedProperty.prototype.constants = constants;

function DataModelCalculatedProperty(options, isParameter) {
	options = options || {};
	var descriptor = {};

	this.define.call(descriptor, 'ParentPath', options);
	this.define.call(descriptor, 'Path', options);
	this.define.call(descriptor, 'Name', options);
	this.define.call(descriptor, 'Direction', options, DataModelPropertyDirection.NONE);
	this.define.call(descriptor, 'Expression', options, this.constants.defaultExpression);

	Object.defineProperty(descriptor, 'Parameters', {
		enumerable: true,
		writable: true,
		value: (options.Parameters || []).filter(function (param) {
			return param.__type !== 2 && !param.localizedValue;
		}),
	});

	Object.defineProperty(descriptor, 'Localization', {
		enumerable: true,
		writable: true,
		value: (options.Parameters || []).filter(function (param) {
			return param.__type === 2 || param.localizedValue;
		}),
	});

	this.define.call(descriptor, 'Type', options);
	this.define.call(descriptor, 'IsArray', options, false);
	this.descriptor = descriptor;
	this.callBacks = {};
	this.isParameter = isParameter === true;
}

DataModelCalculatedProperty.prototype.prepareCode = function () {
	var calcProperty = this;
	var _formatContext = [];
	calcProperty.constants.initilizationCode.replace(/(\$[\w_]+)\s*=\s*([^;]+)/g, function (f, varName, value) {
		_formatContext.push(`formatContext.${varName} = ${value};`);
	});
	var declarations = [calcProperty.constants.initilizationCode];
	declarations.push(`var ${constants.forceValueChanged} = function() { v.forceChanged = true; };`);
	declarations.push(`var ${constants.preventValueChanged} = function() { v.forceChanged = false; };`);
	declarations.push(`var ${constants.triggeredBy} = t;`);
	declarations.push(`var ${constants.currentThemeColors} = mx.currentThemeColors;`);

	calcProperty.descriptor.Localization.forEach(function (param) {
		declarations.push(`var ${param.varName} = "${param.localizedValue.replace(/"/g, '\\"')}";`);
		_formatContext.push(`formatContext.${param.varName} = ${param.varName};`);
	});
	calcProperty.descriptor.Parameters.forEach(function (param) {
		var path = param.localPath ? param.localPath : param.path;
		var targetObj = param.localPath ? 'p' : 'r';
		var initParameter = `${param.varName} = DataModelCalculatedProperty.prototype.getParameter(${targetObj}, "${path}", "${param.name}", "${calcProperty.descriptor.Path}");`;
		declarations.push(`var ${initParameter}`);
		_formatContext.push(`formatContext.${initParameter}`);
	});

	return `/* ${calcProperty.descriptor.Name} [${calcProperty.descriptor.Path}] */
		(function execute(r,p,v,c,t) {
			${declarations.join('')}
			var formatContext = {};
			${_formatContext.join('')}
			var $format = function(){
				var args = [].slice.call(arguments);
				args.push(formatContext);
				return DataModelCalculatedProperty.prototype._format.apply(this,args);
			};

			${calcProperty.descriptor.Expression}

		})`;
};

DataModelCalculatedProperty.prototype._format = function () {
	var args = [].slice.call(arguments);
	var len = args.length;
	if (len > 0) {
		var template = args[0];
		if (len === 1) {
			return template;
		}
		var scope = args[len - 1];
		return template.replace(/{([\w_$]+)}/g, function (m, sd) {
			if (!isFinite(sd)) {
				var value = scope[sd];
				if (value.hasOwnProperty('$value')) {
					value = value.$value;
				}
				return value;
			}
			var d = +sd;
			return args[d + 1];
		});
	}
};

DataModelCalculatedProperty.prototype.register = function (root) {
	root.$calcProperties = root.$calcProperties || new DataModelCalculatedPropertiesInfo();

	var property = root.$calcProperties[this.descriptor.Path];

	if (!property) {
		property = root.$calcProperties[this.descriptor.Path] = this;
	} else if (!this.isParameter) {
		angular.extend(property.descriptor, this.descriptor);
	}

	return property;
};

DataModelCalculatedProperty.prototype.apply = function (root, additionalData) {
	var calcProperty = this;

	var dataModelHelper = new DataModelHelper();

	var parts = calcProperty.descriptor.ParentPath.split('.'),
		pathAfter = '',
		arrayPath = '';
	var curr = root,
		parentArray = null;

	for (var i = 0; i < parts.length; i++) {
		var p = parts[i];

		if (curr.hasOwnProperty(p)) {
			curr = curr[p];
			if (curr instanceof Array) {
				arrayPath = parts.slice(0, i + 1).join('.');
				pathAfter = parts.slice(i + 1).join('.');
				parentArray = curr;
				break;
			}

			if (typeof curr === 'undefined' || curr === null) {
				break;
			}
		}
	}

	if (parentArray) {
		applyInArray(calcProperty, root, parentArray, arrayPath, pathAfter, additionalData);
	} else {
		applyInObject(calcProperty, root, root, calcProperty.descriptor.ParentPath, calcProperty.descriptor.ParentPath, additionalData);
	}

	return calcProperty;

	function applyInArray(calcProperty, root, array, arrayPath, pathInArray, additionalData) {
		var arrayInfo = calcProperty.getParentInfo(root, arrayPath);

		if (arrayInfo.parent !== null) {
			var arrayCalcPropertyOptions = {
				Name: arrayInfo.name,
				Path: arrayPath,
				ParentPath: arrayInfo.parentPath,
				IsArray: true,
			};

			var p = new DataModelCalculatedProperty(arrayCalcPropertyOptions);

			p.isParameter = true;
			var x = p.register(root);
			x.apply.call(x, root, additionalData);
		}

		for (var i = 0; i < array.length; i++) {
			applyInObject(calcProperty, root, array[i], arrayPath, pathInArray, additionalData);
		}
	}

	function applyInObject(calcProperty, root, parentObject, parentObjectPath, localParentPath, additionalData) {
		var parent = localParentPath ? _.get(parentObject, localParentPath) : parentObject;

		if (parent !== null) {
			additionalData = additionalData || {};
			var config = additionalData.config;

			config = config || {}; // Used inside eval. TODO: initialize all necessary config values

			var calcValue = _.get(parent, calcProperty.descriptor.Name + constants.calcValueSuffix);

			if (!calcValue) {
				var defaultValue = parent[calcProperty.descriptor.Name];

				if (typeof defaultValue === 'undefined') {
					defaultValue = calcProperty.getDefaultValueByType();
				}

				calcValue = initValue(parent, calcProperty /*calcProperty.descriptor.Name*/, defaultValue);

				calcProperty.descriptor.Parameters.forEach(function (param) {
					var pathParts = param.path.split('.');

					pathParts.pop();

					var paramProperty = root.$calcProperties[param.path];

					if (!paramProperty) {
						paramProperty = new DataModelCalculatedProperty(
							{
								Name: param.name,
								Path: param.path,
								ParentPath: pathParts.join('.'),
							},
							true
						);

						paramProperty = paramProperty.register(root);
					}

					paramProperty.apply.call(paramProperty, root, additionalData);

					if (param.watchable !== false) {
						paramProperty.onChange(
							calcProperty.descriptor.Path,
							function () {
								recalculate(undefined, parentObject, getCalcValue(calcProperty, parent), calcProperty, config, param.path);
							},
							calcProperty.descriptor.ParentPath !== localParentPath
						);
					}

					if (localParentPath !== parentObjectPath && param.path.indexOf(parentObjectPath + '.') === 0) {
						param.localPath = param.path.substr(parentObjectPath.length + 1);
					}
				});

				var firstGet = evaluatePropertyExpression(parentObject, calcValue, calcProperty, config);
				if (firstGet && typeof firstGet.then === 'function') {
					calcProperty.promiseToWaitBeforeProseed = firstGet.then(function (newResult) {
						calcValue.value = newResult;
						calcValue.oldValue = newResult;
					});
				} else {
					if (firstGet instanceof Array) {
						initArrayValue(firstGet, calcProperty, root, additionalData);
					}
					calcValue.value = firstGet;
					calcValue.oldValue = firstGet;
				}

				if (firstGet instanceof Relation) {
					firstGet.onChange(function () {
						calcProperty.runCallBacks();
					});
				} else if (calcProperty.descriptor.Name !== DataModelSystemProperty.Item || localParentPath === parentObjectPath) {
					/*!calcProperty.descriptor.IsArray*/

					Object.defineProperty(parent, calcProperty.descriptor.Name, {
						enumerable: true,
						configurable: true,
						get: function () {
							return parent[calcProperty.descriptor.Name + calcProperty.constants.calcValueSuffix].value;
						},
						set: function (value) {
							var calcValue = getCalcValue(calcProperty, parent);
							if (calcValue.value !== value) {
								recalculate(value, parentObject, calcValue, calcProperty, config, calcValue.triggeredBy);
							}
						},
					});
				}
			}
		}
		return calcProperty;
	}

	function getCalcValue(calcProperty, parent) {
		return parent[calcProperty.descriptor.Name + calcProperty.constants.calcValueSuffix];
	}

	// parameters are used in scope of dynamic expression
	function evaluatePropertyExpression(parentObject, calcValue, calcProperty, config, triggeredBy) {
		var _getValue;
		try {
			if (typeof calcProperty.execute !== 'function') {
				calcProperty.execute = eval(calcProperty.prepareCode());
			}
			_getValue = calcProperty.execute(root, parentObject, calcValue, config, triggeredBy);
		} catch (e) {
			console.error('Calculated property exception:', {
				Path: calcProperty.descriptor.Path,
				Expression: calcProperty.descriptor.Expression,
				Error: e.message,
			});
		}
		return _getValue;
	}

	function initArrayValue(value, calcProperty, root, additionalData) {
		if (value.$arrayInitialized) {
			return;
		}
		if (value instanceof DataModelArray) {
			value.onChange(function (newValue, fnName, args) {
				if (fnName === 'push' || fnName === 'unshift') {
					if (value && typeof value.getDefaultItem === 'function') {
						args.forEach(function (item) {
							dataModelHelper.initRelations(item, value.getDefaultItem());
						});
					}
					applyToArrayItems(args, calcProperty.descriptor.Path, root, additionalData);
				}
				calcProperty.runCallBacks();
			});
		} else {
			['pop', 'shift', 'splice', 'changed'].forEach(function (fn) {
				arrayOn(fn, value, [
					function () {
						calcProperty.runCallBacks();
					},
				]);
			});

			['push', 'unshift'].forEach(function (fn) {
				arrayOn(fn, value, [
					function () {
						var args = Array.prototype.slice.call(arguments, 0);
						if (value && typeof value.getDefaultItem === 'function') {
							args.forEach(function (item) {
								dataModelHelper.initRelations(item, value.getDefaultItem());
							});
						}
						applyToArrayItems(args, calcProperty.descriptor.Path, root, additionalData);
					},
					function () {
						calcProperty.runCallBacks();
					},
				]);
			});
		}
		Object.defineProperty(value, '$arrayInitialized', {
			value: true,
			enumerable: false,
			writable: false,
		});

		function arrayOn(fnName, object, handlers) {
			var func = function () {
				var args = Array.prototype.slice.call(arguments, 0);

				dataModelHelper.profiler.arrayChanged(calcProperty, fnName);
				if (Array.prototype[fnName]) {
					Array.prototype[fnName].apply(this, args);
					handlers.forEach(function (handler) {
						handler.apply(this, args);
					});
				} else if (fnName === 'changed') {
					handlers.forEach(function (handler) {
						handler.apply(this, args);
					});
				}
			};

			Object.defineProperty(object, fnName, {
				value: func,
				enumerable: false,
				writable: true,
			});
		}
	}

	function applyToArrayItems(items, arrayPath, root, additionalData) {
		_.forOwn(root.$calcProperties, function (property) {
			if (property.descriptor.Path.indexOf(arrayPath + '.') === 0) {
				var pathInArray = property.descriptor.ParentPath.substr(arrayPath.length + 1);

				_.forEach(items, function (item) {
					if (typeof item === 'object') {
						applyInObject(property, root, item, arrayPath, pathInArray, additionalData);
					}
				});
			}
		});
	}

	function initValue(parent, calcProperty, defaultValue) {
		var name = calcProperty.descriptor.Name;
		var calcValue = {
			defaultValue: defaultValue,
			value: defaultValue,
			changed: false,
			oldValue: defaultValue,
			hash: name + '_' + new Date().getMilliseconds(),
		};
		calcValue.__callsResetDebounced = $.debounce(0, function () {
			calcValue.__calls = 0;
		});
		calcValue.__callsInc = function () {
			calcValue.__calls++;
			calcValue.__callsResetDebounced();
		};

		var __calls = 0;
		Object.defineProperty(calcValue, '__calls', {
			enumerable: true,
			get: function () {
				return __calls;
			},
			set: function (value) {
				__calls = value;
			},
		});

		Object.defineProperty(parent, name + constants.calcValueSuffix, {
			configurable: true,
			enumerable: false,
			writable: true,
			value: calcValue,
		});

		if (defaultValue instanceof Array) {
			initArrayValue(defaultValue, calcProperty, root, additionalData);
		}

		return calcValue;
	}

	function recalculate(value, parentObject, calcValue, calcProperty, config, triggeredBy) {
		if (value !== calcValue.value && calcValue.__calls > calcProperty.constants.maximumRecalculationsAllowed) {
			throw new Error('Maximum relcalculations exeeded: ' + calcProperty.descriptor.Path + '. Last call triggered by: ' + triggeredBy);
		} else {
			calcValue.__callsInc();
			var valueChanged = calculatePropertyValue(value, parentObject, calcValue, calcProperty, config, triggeredBy);
			if (valueChanged) {
				notifyListeners(calcValue, calcProperty, root, additionalData);
			}
		}
	}

	function notifyListeners(calcValue, calcProperty, root, additionalData) {
		if (calcValue.value instanceof Array) {
			initArrayValue(calcValue.value, calcProperty, root, additionalData);
			applyToArrayItems(calcValue.value, calcProperty.descriptor.Path, root, additionalData);
		}
		calcValue.changed = true;
		calcProperty.runCallBacks(calcValue.value, calcValue.oldValue);
		calcValue.changed = false;
	}

	function calculatePropertyValue(incomeValue, parentObject, calcValue, calcProperty, config, triggeredBy) {
		var newValue, valueBefore, valueChanged;

		var updateMode = typeof incomeValue === 'undefined';

		// value before recalculation.
		// TODO: not fully implemented yet
		valueBefore = safeValueCopy(calcValue.value);

		if (!updateMode) {
			if (incomeValue instanceof Array) {
				initArrayValue(incomeValue, calcProperty, root, additionalData);
			}
			// prepare scope for expression: $value equals income value
			calcValue.value = incomeValue;
		}

		// prepare scope for expression: $oldValue is the value before recalculation.
		calcValue.oldValue = valueBefore;

		// run expression
		newValue = evaluatePropertyExpression(parentObject, calcValue, calcProperty, config, triggeredBy);

		if (newValue && typeof newValue.then === 'function') {
			dataModelHelper.profiler.propertyChanged(calcValue, calcProperty, triggeredBy, { propertyReturnPromise: true });

			const asyncExecutor = async () => {
				calcValue.value = await newValue;
				delete calcValue.forceChanged;

				dataModelHelper.profiler.propertyChanged(calcValue, calcProperty, triggeredBy, { resultFromPromise: true });

				notifyListeners(calcValue, calcProperty, root, additionalData);
			};
			asyncExecutor();

			return false;
		}

		if (valueBefore instanceof Relation && newValue !== valueBefore) {
			calcValue.value = valueBefore;
			return valueBefore.initNewChanges(newValue);
		} else if (valueBefore instanceof DataModelArray && newValue !== valueBefore) {
			calcValue.value = valueBefore;
			valueBefore.set(newValue);
			return true;
		}

		calcValue.value = newValue;

		// check actual result value changed (compare old and new values)
		valueChanged = safeValueCompare(valueBefore, newValue);
		// check if $forceValueChanged or $preventValueChanged was called
		valueChanged = (valueChanged || calcValue.forceChanged === true) && calcValue.forceChanged !== false;

		dataModelHelper.profiler.propertyChanged(calcValue, calcProperty, triggeredBy, {
			changeForced: calcValue.forceChanged === true,
			changePrevented: calcValue.forceChanged === false,
		});

		delete calcValue.forceChanged;

		return valueChanged;
	}

	function safeValueCompare(oldValue, value) {
		var dateCompare = value instanceof Date && oldValue instanceof Date;

		return dateCompare ? oldValue.toISOString() !== value.toISOString() : value !== oldValue || !_.isEqual(value, oldValue);
	}

	function safeValueCopy(value) {
		// TODO: implement copying of all reference types here
		if (value instanceof Date) {
			return new Date(value);
		}

		if (Array.isArray(value)) {
			// TODO: not implemented yet
			return value;
		}

		if (typeof value === 'object') {
			// TODO: not implemented yet
			return value;
		}

		return value;
	}
};

DataModelCalculatedProperty.prototype.getDefaultValueByType = function getDefaultObjectByType() {
	var defValue = this.descriptor.IsArray ? MxDataModelDefaultValues.ArrayType : MxDataModelDefaultValues[this.descriptor.Type];

	return angular.copy(defValue);
};

DataModelCalculatedProperty.prototype.onChange = function (key, callBack, appendCallback) {
	var calcProperty = this;

	if (key && angular.isFunction(callBack)) {
		if (appendCallback) {
			calcProperty.callBacks[key] = calcProperty.callBacks[key] || [];
			calcProperty.callBacks[key].push(callBack);
		} else {
			calcProperty.callBacks[key] = [callBack];
		}
	}
	return calcProperty;
};

DataModelCalculatedProperty.prototype.runCallBacks = function (value, oldValue) {
	var calcProperty = this;

	_.forEach(calcProperty.callBacks, function (keyCallBacks) {
		_.forEach(keyCallBacks, function (callBack) {
			callBack(value, oldValue);
		});
	});
};

DataModelCalculatedProperty.prototype.triggerChange = function (parent, oldValue) {
	var calcProperty = this;

	var calcValue = parent[calcProperty.descriptor.Name + calcProperty.constants.calcValueSuffix];
	if (calcValue) {
		calcValue.oldValue = oldValue;
		calcValue.changed = true;
		calcProperty.runCallBacks(parent[calcProperty.descriptor.Name], oldValue);
		calcValue.changed = false;
	}
};

DataModelCalculatedProperty.prototype.getParameter = function (parentObject, path, name, triggeredBy) {
	var parameter = {};
	var constants = DataModelCalculatedProperty.prototype.constants;
	var calcValue = _.get(parentObject, path + constants.calcValueSuffix);
	if (calcValue) {
		parameter[constants.valueProperty] = calcValue.value;
		parameter[constants.oldValueProperty] = calcValue.oldValue;
		parameter[constants.hasChangesProperty] = calcValue.changed;
		parameter.hash = calcValue.hash;

		var paramParentInfo = DataModelCalculatedProperty.prototype.getParentInfo(parentObject, path);
		parameter[constants.setValue] = function (value) {
			calcValue.triggeredBy = triggeredBy;
			paramParentInfo.parent[paramParentInfo.name] = value && value.$value ? value.$value : value;
			delete calcValue.triggeredBy;
			parameter[constants.valueProperty] = calcValue.value;
			parameter[constants.oldValueProperty] = calcValue.oldValue;
			parameter[constants.hasChangesProperty] = calcValue.changed;
		};
	}

	return parameter;
};

DataModelCalculatedProperty.prototype.getParentInfo = function (obj, path) {
	var parts = path.split('.');
	var name = parts.pop();
	var parentPath = parts.join('.');

	return {
		parentPath: parentPath,
		parent: parentPath ? _.get(obj, parentPath) : obj,
		name: name,
	};
};
DataModelCalculatedProperty.prototype.define = function (name, source, defaultValue) {
	defaultValue = typeof defaultValue !== 'undefined' ? defaultValue : '';
	Object.defineProperty(this, name, {
		enumerable: true,
		writable: true,
		value: source[name] || defaultValue,
	});
};

DataModelCalculatedPropertiesInfo.prototype.$$applyTo = function (path, root, additionalData) {
	var calcProperties = this;
	var toRunCallbacks = []; // var pathPropertyExists = _get(root, path + constants.calcValueSuffix);

	_.forOwn(calcProperties, function (property) {
		if (property.descriptor.Path.indexOf(path + '.') === 0) {
			property.apply(root, additionalData);
		} else {
			_.some(property.descriptor.Parameters, function (param) {
				if (param.watchable !== false && param.path.indexOf(path + '.') === 0) {
					toRunCallbacks.push(param.path);
					return true;
				}
			});
		}
	});
	toRunCallbacks.forEach(function (path) {
		var property = calcProperties[path];

		if (property) {
			property.runCallBacks();
		}
	});
};

DataModelCalculatedPropertiesInfo.prototype.$$merge = function (parent, property, data, path) {
	var that = this;
	return merge(parent, property, data, path);

	function merge(parent, property, data, path) {
		if (Array.isArray(data)) {
			if (parent[property] && parent[property] instanceof DataModelArray) {
				parent[property].set(data);
			} else {
				parent[property] = data; // ?? .slice(0);
			}
		} else if (typeof data === 'object' && typeof parent[property] === 'object') {
			// && data !== null) {
			if (data === null) {
				// Will not destroy existing objects (usecases???)
				//We need to check inner calcFields exist. Usecase : http://localhost:4000/app-solutionbuilder/designer/PDRContentWidgetTypeWizard/fb5d75a3-3ef8-c104-ca1f-08d3ac901554
				parent[property] = data;
				return;
			}

			if (data instanceof Relation || data instanceof DataModelArray) {
				parent[property] = data;
				return;
			}

			var calcProperty = that[path];
			if (calcProperty && !calcProperty.isParameter) {
				if (typeof parent[property] === 'object') {
					console.warn('Warning!. Data model property `' + calcProperty.descriptor.Path + '` may contain child properties which could be ignored during data update');
					// TODO: handle inner $calcValue properies here
				}
				parent[property] = data;
			} else {
				var objOldValue = angular.copy(parent[property]);

				parent[property] = parent[property] || {};
				_.forOwn(data, function (value, key) {
					// Deep processing
					merge(parent[property], key, value, path + '.' + key);
				});

				if (calcProperty) {
					// Manualy trigger change callbacks for Object, because there is no direct Set call
					calcProperty.triggerChange(parent, objOldValue);
				}
			}
		} else {
			// Assign value directly and call calc property setter if it exists
			parent[property] = data;
		}
	}
};

function DataModelCalculatedPropertiesInfo() {}

export { DataModelCalculatedProperty, DataModelCalculatedPropertiesInfo, DataModelSystemProperty };
